import { type ReactNode } from 'react';

import { type ResolvedContextData } from '@common/defaults/ContextData';
import { type Props as BrandedHeadingProps } from '@web/atoms/BrandedHeading';
import { OptionalLink } from '@web/atoms/OptionalLink';
import { Widget } from '@web/molecules/Widget';
import { WidgetHeader } from '@web/molecules/Widget/atoms/WidgetHeader';
import { WidgetHeaderSection } from '@web/molecules/Widget/atoms/WidgetHeaderSection';
import { WidgetTitle } from '@web/molecules/Widget/atoms/WidgetTitle';

interface Props {
    children?: ReactNode;
    contextData: ResolvedContextData;
    footer?: ReactNode;
    header?: ReactNode;
    isPremium?: string;
    isTabbedWidget?: boolean;
    title: string;
    titleAccentColor?: BrandedHeadingProps['accentColor'];
    titleIcon?: BrandedHeadingProps['icon'];
    titleUrl?: string;
    titleVariant?: BrandedHeadingProps['variant'];
}

/**
 * SimpleWidget is a widget organism/template that is used to render a sidebar component
 * with a title, header, content and footer.
 */
export const SimpleWidget = ({
    contextData,
    title,
    titleUrl,
    titleAccentColor,
    titleIcon,
    titleVariant,
    isTabbedWidget,
    isPremium,
    header,
    footer,
    children,
}: Props) => {
    if (isTabbedWidget) {
        return children;
    }

    return (
        <Widget isPremium={!!isPremium} title={title}>
            <WidgetHeader>
                <WidgetHeaderSection>
                    <OptionalLink className="sidebar-heading" href={titleUrl}>
                        <WidgetTitle
                            contextData={contextData}
                            accentColor={titleAccentColor}
                            icon={titleIcon}
                            variant={titleVariant}
                        >
                            {title}
                        </WidgetTitle>
                    </OptionalLink>
                    {header}
                </WidgetHeaderSection>
            </WidgetHeader>
            {children}
            {footer}
        </Widget>
    );
};
